/* ============================================
   EMAIL TEMPLATES — SENDING THE QUOTE
   Asset: email-tpl-quoting.js
   CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-18)

   The email that carries a quote to a customer for the first time. Loads after
   email-tpl-core.js and email-tpl-blocks.js.
   ============================================ */
(function () {
  'use strict';

  var T = window.RQDTpl;
  var B = window.RQDTplBlocks;
  if (!T || !B) { console.error('[email-tpl] email-tpl-quoting.js loaded before the kernel'); return; }

  /* Bound to the same names the body used when it lived in one file, so the proven code
     below is a MOVE and not a rewrite. Anything new should call T./B. directly. */
  var _loadDetails = T.loadDetails, _loadMentionIndex = T.loadMentionIndex,
      _loadRepSettings = T.loadRepSettings, _ensureChain = T.ensureChain,
      _dealByRef = T.dealByRef, _chainDeals = T.chainDeals,
      _firstName = T.firstName, _stateName = T.stateName, _shipZip = T.shipZip,
      _shipDestination = T.shipDestination,
      _whenPhrase = T.whenPhrase, _lastSpokenCall = T.lastSpokenCall,
      _lastCustomerInboundAfter = T.lastCustomerInboundAfter,
      _lastCustomerOutboundAfter = T.lastCustomerOutboundAfter,
      _lastNoAnswerCall = T.lastNoAnswerCall, _displayName = T.displayName,
      _linkForLine = T.linkForLine, _mostExpensiveLine = T.mostExpensiveLine,
      _inquiryProducts = T.inquiryProducts, _categoryPhrase = T.categoryPhrase,
      _attachedQuoteRefs = T.attachedQuoteRefs, _liveFees = T.liveFees,
      _floorColorLabel = T.floorColorLabel,
      _feeState = T.feeState, _freightSentences = T.freightSentences,
      _joinRuns = T.joinRuns, _INQUIRY_ITEM_CAP = 12;

  /* CUSTOM_UPDATEDQUOTE_BY_CLAUDE (Ryan 2026-09-24) — THE HEADLINE PRODUCT(S) AND THE COLOUR
     CLAUSE, shared by the 1st and the 2nd+ quote emails. A MOVE out of _buildFirstQuote, not a
     rewrite: every rule in here is the first-quote email's and is dated where it was made, and it
     lives in one place because the colour rule has three conditions that two copies would drift on.
     `warnings` is appended to in place. Returns { tops: [{title, url, line}], colorPhrase }. */
  function _headlineItems(perQuote, index, warnings) {
    /* One headline item PER attached quote, deduped: two revisions of the same job
       usually lead with the same product, and naming it twice reads like a mistake.
       Identity is the handle when there is one (a title can be edited per revision). */
    var tops = [], topSeen = {}, unlinked = [];
    perQuote.forEach(function (d) {
      var lines = (d.quote && Array.isArray(d.quote.lineItems)) ? d.quote.lineItems : [];
      var top = _mostExpensiveLine(lines);
      if (!top) return;
      /* Dedupe on the DISPLAYED name as well as the handle: once the variant tail is
         stripped, two different products can print identically, and "the Rubber Roll and
         Rubber Roll" reads as a mistake. */
      var shown = _displayName(top.title);
      var key = String(top.handle || shown).toLowerCase();
      var nameKey = shown.toLowerCase();
      if (!key || topSeen[key] || topSeen['name:' + nameKey]) return;
      topSeen[key] = 1;
      topSeen['name:' + nameKey] = 1;
      var url = _linkForLine(top, index);
      if (!url) unlinked.push(shown);
      /* The LINE rides along, not just its name: the colour clause below reads `top.color`
         off it. _joinRuns only ever touches .title/.url, so the extra key is inert there. */
      tops.push({ title: shown, url: url, line: top });
    });
    if (unlinked.length) {
      warnings.push(unlinked.map(function (t) { return '"' + t + '"'; }).join(', ')
        + (unlinked.length === 1 ? ' is' : ' are') + ' not in the product index, so '
        + (unlinked.length === 1 ? 'it went' : 'they went') + ' in without a link.');
    }
    if (!tops.length) warnings.push('No priced line items found, so the item sentence was left out.');

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26) — "…in the Grippin Gray color."

       Ryan: *"If we're sending 1 flooring type… I want the sentence changed to 'I've attached
       your quote for the Ebb & Flow Motivate Laminate Roll in the [color] color.'… if we're
       writing in the email and it includes 2 flooring products referenced in that sentence,
       then we don't need to include colors on top of that. That's overkill."*

       So the clause appears only when it can be read one way, and that takes THREE conditions,
       not one:
         1. the sentence names exactly ONE product (his rule — two named products, no colours);
         2. that product is ECORE flooring with a colour (the worker only stamps `color` on
            those — see lib/flooring-color.js for why Legend is deliberately excluded);
         3. every flooring line on the attached quote(s) resolves to that SAME colour.

       ⛔ CONDITION 3 IS NOT THE SAME AS CONDITION 1, and it is the one the phrasing hides. The
       sentence names one product because `tops` is one headline item PER QUOTE — but a single
       quote often carries several flooring lines, and 8 of the 93 ECORE quotes in a 200-deal
       sample carry TWO different colours (a platform in one, the perimeter in another). Gate
       only on "one product named" and those eight emails would tell the buyer their whole
       floor is Crema on the strength of the most expensive line. Colours are counted across
       every attached quote, so the clause drops itself rather than narrowing a two-colour job
       to one. Silent by Ryan's call — the rep needs no warning for a sentence that simply
       says less. */
    var colorPhrase = '';
    if (tops.length === 1) {
      var seenColors = {}, colorCount = 0;
      perQuote.forEach(function (d) {
        var lines = (d.quote && Array.isArray(d.quote.lineItems)) ? d.quote.lineItems : [];
        lines.forEach(function (li) {
          var lab = _floorColorLabel(li && li.color);
          if (!lab || seenColors[lab.toLowerCase()]) return;
          seenColors[lab.toLowerCase()] = 1;
          colorCount++;
        });
      });
      var ownColor = _floorColorLabel(tops[0].line && tops[0].line.color);
      if (ownColor && colorCount === 1) colorPhrase = ' in the ' + ownColor + ' color';
    }
    return { tops: tops, colorPhrase: colorPhrase };
  }

  /* CUSTOM_UPDATEDQUOTE_BY_CLAUDE (Ryan 2026-09-24) — IS THERE A QUOTE TO SEND? The first check
     of both quote emails' gates, in one place: built (`quoted`), on this email (`quoteAttached`),
     or buildable (`quotable`). See the long notes on each in email-tpl-core.js — this helper is
     only the conjunction, and tests/first-quote-gate.test.mjs pins it to exactly one copy. */
  function _noQuoteToSend(st) {
    if (!st.quoted && !st.quoteAttached && !st.quotable) return 'Available once a quote has been built for this deal, or one is attached to this email.';
    return null;
  }

  /* CUSTOM_ROLLSENTENCE_BY_CLAUDE (Ryan 2026-09-24) — THE TWO SENTENCES THAT FOLLOW AN OVERAGE
     LAYOUT, and which of them a quote has earned. Ryan: *"'The layout takes into consideration the
     minimum required roll lengths from the factory.' can be excluded when the quote is for ECORE
     Heritage Motivate (planks)."* It is a sentence about ROLLS: a plank, a tile or a paver has no
     factory roll length, so on those quotes only the red-overage sentence goes.
     ⛔ THE TEST IS THE `unit:` TAG, read off the mention index (`sqft` is how merchandising tags
     every roll; planks are `unit:plank`, checked on the live storefront 2026-09-24), never a word in
     a title. ⚠️ FAILS TOWARDS TODAY'S WORDING: when no line on the attached quotes can be resolved
     in the index at all, both sentences print as they always have and the rep sees the draft — an
     index that failed to load must not silently trim a roll quote. One helper, both quote emails. */
  function _overageSentences(perQuote, index) {
    var anyRoll = false, anyKnown = false;
    (perQuote || []).forEach(function (d) {
      var lines = (d && d.quote && Array.isArray(d.quote.lineItems)) ? d.quote.lineItems : [];
      lines.forEach(function (li) {
        var u = T.lineUom(li, index);
        if (u) anyKnown = true;
        if (u === 'sqft') anyRoll = true;
      });
    });
    var roll = (anyRoll || !anyKnown)
      ? ' The layout takes into consideration the minimum required roll lengths from the factory.'
      : '';
    return roll + ' All material overage is highlighted in red.';
  }

  /* CUSTOM_LAYOUTPLURAL_BY_CLAUDE (Ryan 2026-10-10, RW-1379-A/-B/-C) — the layout sentence when
     SEVERAL quotes go out together. "in the attachment ... the last page" was written for one
     quote; with three proposals each carrying its own drawing, Ryan's approved wording is
     "I've also included a room layout on the last page of each quote, showing the flooring
     quoted." The flooring clause only when the attached drawings DIFFER (their X-BSF-Layout-Key
     fingerprints), i.e. each version really shows its own flooring. One quote: unchanged. */
  function _layoutLead(metas, pages, updated) {
    var a = updated ? 'an updated room layout' : 'a room layout';
    var pageWord = pages > 1 ? 'pages' : 'page';
    if (!metas || metas.length < 2) {
      return "I've also included " + a + ' in the attachment, which you can see on the last ' + pageWord + '.';
    }
    var keys = [];
    metas.forEach(function (m) {
      var k = String((m && m.layoutKey) || '');
      if (k && keys.indexOf(k) === -1) keys.push(k);
    });
    return "I've also included " + a + ' on the last ' + pageWord + ' of each quote'
      + (keys.length > 1 ? ', showing the flooring quoted.' : '.');   // Ryan 2026-10-10: "flooring quoted", not "flooring color quoted"
  }

  function _buildFirstQuote(ctx) {
    var ref = String((ctx && ctx.ref) || '').trim();
    /* Every quote actually going out, not just the composer's own deal: a rep sending
       -A and -B together is naming an item from each. Falls back to the deal when
       nothing is attached yet, which is the normal order for a rep who writes first. */
    var attachedRefs = _attachedQuoteRefs((ctx && ctx.attachments) || [], ref);
    var readRefs = attachedRefs.length ? attachedRefs : (ref ? [ref] : []);
    return Promise.all([
      Promise.all(readRefs.map(_loadDetails)),
      _loadDetails(ref),          // memoised — free when ref is already in readRefs
      _loadMentionIndex(),
      _ensureChain(ref),          // so _dealByRef can see the call log on the Workstation
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-13) — the ECORE manual list, for the closing
         install line. In the SAME parallel read rather than chained after it: this email already
         waits on four reads and a fifth in series would be felt every time the picker re-renders.
         ⚠️ Resolves to NULL when the page cannot be read, and the line simply does not appear —
         nothing else in the email depends on it. */
      T.loadEcoreDocs(),
      /* CUSTOM_MISSEDCALL_BY_CLAUDE (Ryan 2026-09-20) — the rep's own number, for the ONE
         missed-call opening that quotes it ("you likely received a missed call from me: …").
         Memoised in the kernel, so after the first template of the session this read is free;
         in the same parallel batch for the reason the ECORE read gives above. */
      _loadRepSettings(),
      /* 💳 the payment sentence's link and terms (Ryan 2026-10-27); a refusal (fees pending, closed) resolves to its
         reason so the email still builds without the sentence. */
      B.payLinkFor(ctx).then(function (p) { return { p: p }; }, function (e) { return { error: (e && e.message) || 'the payment link could not be read.' }; }),
    ]).then(function (res) {
      var perQuote = (res[0] || []).filter(Boolean);
      var details = res[1] || perQuote[0] || null;
      var index = res[2] || [];
      var ecoreIdx = res[4] || null;
      var repSettings = res[5] || {};
      var pay = res[6] || {};
      var quote = (details && details.quote) || null;
      var wm = (details && details.workstationMeta) || null;
      var dq = _dealByRef(ref);
      var warnings = [];

      /* Name: the draft order's customer, then the dashboard row, then the To address.
         q.customer on the dashboard row is the same person under a different key. */
      var fullName = (quote && quote.customer && quote.customer.name)
        || (dq && dq.customer) || '';
      var first = _firstName(fullName);
      if (!first) {
        var to = (Array.isArray(ctx && ctx.toEmails) ? ctx.toEmails : [])[0] || '';
        first = _firstName(String(to).split('@')[0].replace(/[._-]+/g, ' '));
      }

      /* Nothing attached yet reads as the one quote this email is obviously about. The
         modal re-renders the template if a second quote lands while the body is untouched. */
      var plural = attachedRefs.length > 1;
      var quoteWord = plural ? 'quotes' : 'quote';

      var _items = _headlineItems(perQuote, index, warnings);
      var tops = _items.tops, colorPhrase = _items.colorPhrase;

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26) — WHICH pleasantry opens this email, and
         whether it opens at all. Three rules, and each one exists because the old single `if
         (call)` got a real email wrong.

         1. ⛔ A STALE CALL IS WORSE THAN NO CALL. The line used to fire on any spoken call ever
            logged, so a quote sent three weeks later opened "Thanks again for your time on the
            phone on the 10th." Ryan: *"anything beyond 'last [fill in the blank]' means that
            line is disqualified… we're not going to email someone saying 'thanks very much for
            your time on the phone back in May of last year' haha, that sounds ridiculous."*
            The cut is `whenPhrase().recent` — the ladder's own flag, so this template does not
            own a second opinion about what "recently" means (see the house-ladder note there).

         2. ⛔ A NEWER INBOUND EMAIL RETIRES THE CALL LINE, REPLIED TO OR NOT. Ryan: *"this
            customer has since emailed me since my phone call, so it should queue off that newer
            email as opposed to the phone call that happened prior to it."* Thanking someone for
            a call they have already followed up on in writing reads as if we never opened the
            reply. So the branch below is on the EMAIL first, and the call is only consulted
            when no email came after it.

         3. ⛔ AND IF WE ALREADY WROTE BACK, THERE IS NOTHING LEFT TO THANK THEM FOR. Ryan: *"if
            there was an email between the phone call which we DID reply to, it should again skip
            that line and just start with 'I've attached your quote for the…'"* The pleasantry is
            an acknowledgement; once the reply exists the acknowledgement has been made, and
            repeating it in the quote email is the second time we thank them for one message.
            ⚠️ Note this collapses to NO LINE, not back to the call line — rule 2 already
            retired that, and falling through to it would resurrect the case rule 2 removed.

         `since` is the call when there is one, else 0 — so a deal that only ever had email
         still finds its unanswered inbound. The email being composed is not a note entry yet,
         so it can never count as its own reply. */
      /* ⛔ THE CUSTOMER'S MAIL, NOT THE DEAL'S. A deal's feed carries the vendor traffic too, and
         on RW-1296-A that broke this both ways at once: the newest inbound was ECORE's freight
         reply (so an unfiltered read would thank the buyer for a message the vendor sent), and the
         rep's 14:53 freight REQUEST sat 91 minutes after the buyer's 13:22 email (so it read as
         the reply and cancelled the acknowledgement). Ryan: *"He emailed me today at 7:22am and I
         did not respond. Shouldn't the opening line reference his email?"*
         See _lastCustomerInboundAfter for why these are separate functions from the ladder's. */
      var chain = _chainDeals(ref).concat(dq ? [dq] : []);
      var custAddrs = T.customerAddrs(quote && quote.customer, dq, ctx && ctx.toEmails);
      var call = _lastSpokenCall(chain);
      var callWhen = call ? _whenPhrase(call.ts, T.now()) : null;
      var sinceCall = call ? call.ts : 0;
      var inbound = _lastCustomerInboundAfter(chain, sinceCall, custAddrs);
      var repliedToInbound = inbound ? !!_lastCustomerOutboundAfter(chain, inbound.ts, custAddrs) : false;

      /* CUSTOM_COLDQUOTE_BY_CLAUDE (Ryan 2026-09-20) — THE QUOTE THAT ANSWERS AN INQUIRY AND
         NOTHING ELSE. With no call and no email on the deal this email opened straight into
         "I've attached your quote for the X" — no acknowledgement that they had asked us for
         anything, which on a website RFQ is the single commonest way this template goes out.
         Ryan's wording: "Thank you for reaching out and inquiring about the X. I've attached
         your requested quote to this email. Please confirm receipt."

         ⛔ THE TEST IS "NOTHING HAS HAPPENED YET", NOT "NO RECENT CALL". The three branches
         below already decide the PLEASANTRY and each is right about its own case; what was
         missing is a case they all fall through. So this asks a wider question than they do —
         any customer email in EITHER direction, ever (`since: 0`), and any call at all, spoken
         or unanswered — because every one of those means something DID happen and the sentence
         would be describing a silence that is not there. A stale connected call and an already
         answered email keep today's plain wording, deliberately: they are not cold, and the
         new sentence would thank somebody for getting in touch a month after we spoke.
         ⚠️ It reads a real gap the other way: a rep who called or wrote OFF the portal and
         logged nothing gets "Thank you for reaching out" on a deal where WE did the reaching.
         Logging the call is the fix; the composer cannot see what the feed was never told. */
      var everEmailed = !!(_lastCustomerInboundAfter(chain, 0, custAddrs)
        || _lastCustomerOutboundAfter(chain, 0, custAddrs));
      var missed = _lastNoAnswerCall(chain);
      var everCalled = !!(call || missed);
      var coldInquiry = !everEmailed && !everCalled;

      /* CUSTOM_MISSEDCALL_BY_CLAUDE (Ryan 2026-09-20) — THE CALL THAT WENT UNANSWERED, AND WHY
         NO VOICEMAIL WAS LEFT.
         ────────────────────────────────────────────────────────────────────────────────────
         Until now this template had exactly ONE call opening — the one for a call they
         ANSWERED — so a quote sent the morning after a voicemail read as though we had never
         picked up the phone. Ryan's six new openings (2026-09-20) are `_missedOpening` below.

         ⛔ ONLY WHEN NOBODY HAS SPOKEN AND NOBODY HAS WRITTEN. Ryan scoped these himself:
         *"where there's been no email from/to the customer yet, and no connected phone call to
         the customer yet."* So a deal that has a spoken call keeps the spoken branch however
         many times we have rung since — "I called you yesterday regarding your inquiry" reads
         as though we had never talked, and we have — and a deal with any customer mail in
         either direction keeps the mail branches. That leaves this for the one shape it is
         written about: we rang a customer who has only ever filled in the form, and missed them.

         ⚠️ `recent` IS THE HOUSE LADDER'S OWN CUT, not a second opinion about "lately" — the
         same flag that disqualifies the spoken line past about a fortnight. Ryan's exception for
         every one of these is the same sentence: *"if the {when} is stale, then we do nothing
         and just go with the regular 'I've attached your quote for the…'"* */
      var missedWhen = missed ? _whenPhrase(missed.ts, T.now()) : null;
      var missedOpens = !!(missed && missedWhen && missedWhen.recent && !call && !everEmailed);

      /* Live Workstation state wins over the saved record when the composer is open on
         that page and on THIS ref (see _liveFees) - it is the array the FREE/Pending
         selector writes to, and it is not one KV read-after-write behind. */
      var liveFees = _liveFees(ref);
      var feeSource = liveFees ? { rawFees: liveFees } : wm;
      var ship = _feeState(feeSource, 'Shipping & Freight', quote ? quote.shippingAmount : null);
      var inst = _feeState(feeSource, 'Installation', quote ? quote.installationAmount : null);
      /* With several quotes on the email the freight sentence can only say one thing.
         When they agree it is simply true; when they disagree the composer's own deal
         wins and the rep is told, because silently picking one would be a claim about
         the others that nobody checked. */
      if (perQuote.length > 1) {
        var states = {};
        perQuote.forEach(function (d) {
          var r = d.quote && d.quote.reference;
          var lf = _liveFees(r);
          states[_feeState(lf ? { rawFees: lf } : d.workstationMeta, 'Shipping & Freight',
            d.quote ? d.quote.shippingAmount : null).status] = 1;
        });
        if (Object.keys(states).length > 1) {
          warnings.push('The attached quotes do not agree on freight, so the shipping line describes '
            + (ref || 'this deal') + ' only. Check it before sending.');
        }
      }
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — name the DESTINATION, because it is not
         always the reader's own location: *"Sometimes, we'll sell to architects and construction
         companies and we'll actually ship to their clients locations."* */
      var shipDest = _shipDestination(quote && quote.shippingAddress);
      if (ship.status !== 'none' && !shipDest && quote && quote.shippingAddress) {
        warnings.push('No city or state on the shipping address, so the sentence says "your location" instead of naming the destination.');
      }

      /* CUSTOM_MISSEDCALL_BY_CLAUDE (Ryan 2026-09-20) — WHY WE LEFT NO VOICEMAIL, IN THE
         CUSTOMER'S OWN EMAIL. One sentence per reason, Ryan's wording, from the `callDetail`
         chip the Deal Dashboard stamps on a "Didn't leave VM" call.

         ⛔ ONLY FOR A CALL THAT SAYS NO VOICEMAIL WAS LEFT (`callVm === 'none'`). A `left` call
         has nothing to explain, and an entry with NO `callVm` at all predates the voicemail
         question entirely — so it gets the plain "I called you" sentence and no claim either
         way, which is the same safe direction the inquiry template already takes.
         ⚠️ An unrecognised `callDetail` leaves `missedReason` empty and the email falls to the
         one-paragraph shape. The reason is prose in a customer's inbox; a value this file does
         not know is a value it must not try to describe. */
      var missedReason = '';
      var missedReasonAfter = '';
      var missedReasonNeedsPhone = false;
      if (missedOpens && missed.callVm === 'none') {
        var _cd = String(missed.callDetail || '');
        if (_cd === 'mailbox-full') {
          missedReason = "I wasn't able to leave a message as the recording said your mailbox was full.";
        } else if (_cd === 'no-vm-box') {
          /* The only reason sentence that quotes a number, and the only one that can degrade:
             with no phone on the rep's profile it keeps the sentence and drops the colon rather
             than mailing a customer "a missed call from me: ." */
          var _repPhone = String(repSettings.repPhone || '').trim();
          missedReason = _repPhone
            ? "I wasn't able to leave a voicemail but you likely received a missed call from me: " + _repPhone + '.'
            : "I wasn't able to leave a voicemail but you likely received a missed call from me.";
          missedReasonNeedsPhone = !_repPhone;
        } else if (_cd === 'after-hours') {
          missedReason = "I didn't leave a message as the recording stated it was outside of business hours.";
        } else if (_cd === 'cut-off') {
          missedReason = "I began to leave a voicemail but I think the line dropped halfway through, so I'm not sure if you received anything from me.";
        } else if (_cd === 'wrong-number') {
          /* CUSTOM_INQUIRYFOLLOWUP_BY_CLAUDE (Ryan 2026-09-26) — the fifth reason. No leading "I",
             so the clause join below leaves it whole: "I called you yesterday, but we weren't able
             to connect." and the question rides AFTER the full stop, reading back the number as it
             is on file — the point is to show them what they typed. */
          missedReason = "we weren't able to connect.";
          var _custPhone = T.customerPhone(details, dq);
          missedReasonAfter = _custPhone ? ' Is the best number for you ' + _custPhone + '?'
            : ' What is the best number to reach you at?';
        }
      }
      if (missedReasonNeedsPhone) {
        warnings.push('Your phone number is not on your rep profile, so the opening line tells them you rang without giving the number it came from.');
      }
      /* The reason as a CLAUSE rather than a sentence, for Ryan's ", but …" join. Every one of
         the four starts with "I", which would read "I called you a couple of days ago, but I
         wasn't able…", so the pronoun is dropped and the full stop trimmed.
         ⚠️ TWO OF THE FOUR CARRY THEIR OWN "but" ("…a voicemail but you likely received…",
         "…a voicemail but I think the line dropped…"), which after this join would read
         "…, but wasn't able to leave a voicemail but you likely…". Those two are joined with
         " and " instead, which is also the truer connective: beginning a voicemail and the line
         dropping is a sequence, not a contrast. ⛔ The reason SENTENCES themselves are untouched
         — they are Ryan's words and the fix is the joint, not the copy. */
      var missedReasonClause = missedReason.replace(/^I\s+/, '').replace(/\.$/, '');
      var missedReasonJoin = / but /.test(missedReasonClause) ? ' and ' : ', but ';

      /* The lead clause. "just now" is the one rung of the house ladder that moves in front of
         the verb — the same transformation the inquiry follow-up makes, so the two emails say
         "I just left you a voicemail" in one voice rather than two. */
      var missedLead = '';
      if (missedOpens) {
        if (missed.callVm === 'left') {
          missedLead = missedWhen.just ? 'I just left you a voicemail'
            : 'I left you a voicemail' + (missedWhen.phrase ? ' ' + missedWhen.phrase : '');
        } else {
          missedLead = missedWhen.just ? 'I just called you'
            : 'I called you' + (missedWhen.phrase ? ' ' + missedWhen.phrase : '');
        }
      }
      /* ⚠️ A `var fn = function` rather than a declaration: a function DECLARED here and called
         from the branches below would be fine today and is one tidy-up away from the block-scope
         ReferenceError tests/block-scoped-function-tests.mjs exists to catch. */
      /* CUSTOM_MISSEDTHANKS_BY_CLAUDE (Ryan 2026-09-20) — ⛔ THE SIGN-OFF SAYS "Thanks again",
         AND ON THIS SHAPE NOTHING HAD THANKED THEM YET. Ryan: *"I'm pretty sure a lot of these
         templates never said 'Thanks' initially to then make 'Thanks again' make sense —
         especially when we've never spoken to the person."*

         ⚠️ RYAN'S SECOND PHRASING IS THE ONE THAT SHIPPED ("that sounds better"): the THANKS
         LEADS and names the product, and the call drops to a subordinate clause —
         *"Thanks very much for reaching out about the Performance Rally Rubber Roll in the
         Raiders color. I called you a little bit ago, but I didn't leave a message as the
         recording stated it was outside of business hours."*
         So the product moved OUT of the call sentence: `missedLead` is now used bare ("I called
         you a couple of days ago") rather than carrying "regarding your inquiry for the …". */
      var missedThanksRuns = function () {
        /* No priced line to name falls back to the bare thanks rather than "reaching out about
           the ." — the inquiry template makes the same call in the same situation. */
        if (!tops.length) return [{ t: 'Thanks very much for reaching out.' }];
        return [{ t: 'Thanks very much for reaching out about the ' }]
          .concat(_joinRuns(tops), [{ t: colorPhrase + '.' }]);
      };
      /* "Attached you'll find a quote for this product; please confirm receipt."
         ⚠️ TWO DIFFERENT COUNTS IN ONE SENTENCE, and they are not the same number: a quote /
         quotes follows how many PDFs are attached (`plural`), this product / these products
         follows how many products the sentence just NAMED (`tops`). One quote can name two
         products and two revisions can name one. */
      var missedAttachSentence = "Attached you'll find " + (plural ? 'quotes' : 'a quote')
        + (tops.length ? ' for ' + (tops.length > 1 ? 'these products' : 'this product') : '')
        + '; please confirm receipt.';

      /* CUSTOM_STALECALL_BY_CLAUDE (Ryan 2026-09-20) — a conversation too old for the house
         ladder to name is still a conversation. It used to produce NO opening at all, so a
         quote following a call three weeks ago read like a quote to a stranger; it now leads
         the attachment sentence instead of standing above it, because there is no date to give
         and "As per our last conversation" IS the reference.
         ⛔ AND AN INBOUND EMAIL SINCE THAT CALL RETIRES IT (Ryan). That is `!inbound`, which
         holds by construction here — the `inbound` branch below runs first — and is written out
         so the rule survives somebody reordering these branches. */
      var staleSpoken = !!(call && callWhen && !callWhen.recent && !inbound);
      var attachLead = staleSpoken
        ? "As per our last conversation, I've attached your "
        : "I've attached your ";

      /* Computed BEFORE the paragraphs because one shape folds it into the attachment sentence
         instead of giving it a paragraph of its own — see the merge below. */
      var freight = _freightSentences({
        ship: ship, inst: inst, destination: shipDest,
        names: tops.map(function (t) { return t.title; }),
        quoteCount: attachedRefs.length || 1,
      });
      var freightText = freight.length ? freight.join(' ') : '';

      var paras = [];
      paras.push([{ t: first ? 'Hi ' + first + ',' : 'Hi there,' }]);

      if (inbound) {
        /* Their email is the newest thing on the thread. Thank them for it only if it is still
           waiting on us; no time phrase, because the thanks is for the message, not its date. */
        if (!repliedToInbound) paras.push([{ t: 'Thanks very much for your email.' }]);
      } else if (call && callWhen && callWhen.recent) {
        paras.push([{ t: 'Thanks again for your time on the phone ' + callWhen.phrase + '.' }]);
      } else if (missedOpens && missedReason) {
        /* Ryan's line break: with a reason to give, the thanks, the call and the explanation are
           their own paragraph and the attachment follows below. Without one they are a single
           sentence pair and stay on one line — see the next block.
           ⚠️ THE JOIN IS ", but " AND THE REASON'S LEADING "I" IS DROPPED, or it reads "I called
           you a couple of days ago, but I wasn't able…". See `missedReasonClause`. */
        paras.push(missedThanksRuns().concat([{ t: ' ' + missedLead + missedReasonJoin + missedReasonClause + '.' + missedReasonAfter }]));
      }

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — "I've attached your quote…", and the
         confirm-receipt ask rides HERE now.

         It used to read "Please see the attached quote for the X." and the receipt ask lived in
         a paragraph of its own further down, which put a request in the middle of an email that
         ends on a payment link. Attaching the quote and asking them to confirm it arrived are
         one thought, so they are now one paragraph, and the door-open line at the bottom is
         purely that.

         `quoteWord` still drives the plural off the number of quotes ACTUALLY ATTACHED, so two
         revisions on one email read "your quotes for the A and B". */
      /* CUSTOM_COLDQUOTE_BY_CLAUDE (Ryan 2026-09-20) — on a cold inquiry the thank-you and the
         attachment are ONE paragraph, not a pleasantry above this one. That is Ryan's own
         wording and it is also why the product moves sentence: naming it in the thank-you is
         what makes the sentence about THEIR inquiry rather than about our filing. The colour
         clause and the linked product runs ride with it, so a single-colour ECORE quote still
         reads "…inquiring about the Ebb & Flow Motivate Laminate Roll in the Crema color."
         ⚠️ With no priced line to name, the thank-you keeps its first half and drops the
         product — never "inquiring about the ." */
      /* CUSTOM_MISSEDCALL_BY_CLAUDE (Ryan 2026-09-20) — ⛔ THE FREIGHT LINE JOINS THIS SENTENCE
         ON THE REASON SHAPE, and that is Ryan's instruction, not a tidy-up: *"add the line about
         'The quote includes the cost of shipping to…' to this line instead of having another
         line break between it."* That shape already spends a paragraph on the call and the
         explanation, so leaving the attachment alone on a line of its own and the freight alone
         on the next gives a four-line email three of whose lines are one sentence each. */
      var mergeFreightIntoAttach = !!(missedOpens && missedReason);
      if (missedOpens && missedReason) {
        paras.push([{ t: missedAttachSentence + (freightText ? ' ' + freightText : '') }]);
      } else if (missedOpens) {
        /* No reason to give, so the thanks, the call and the attachment are ONE paragraph — Ryan
           wrote these as a single line and put the break only in the four that explain. */
        paras.push(missedThanksRuns().concat([{ t: ' ' + missedLead + '. ' + missedAttachSentence }]));
      } else if (coldInquiry && tops.length) {
        paras.push([{ t: 'Thank you for reaching out and inquiring about the ' }]
          .concat(_joinRuns(tops), [{ t: colorPhrase + ". I've attached your requested " + quoteWord
            + ' to this email. Please confirm receipt.' }]));
      } else if (coldInquiry) {
        paras.push([{ t: "Thank you for reaching out. I've attached your requested " + quoteWord
          + ' to this email. Please confirm receipt.' }]);
      } else if (tops.length) {
        paras.push([{ t: attachLead + quoteWord + ' for the ' }]
          .concat(_joinRuns(tops), [{ t: colorPhrase + '. Please confirm receipt.' }]));
      } else {
        paras.push([{ t: attachLead + quoteWord + '. Please confirm receipt.' }]);
      }

      if (freightText && !mergeFreightIntoAttach) paras.push([{ t: freightText }]);

      /* 💳 THE PAYMENT SENTENCE (Ryan 2026-10-27), B.paymentSentence with the 1st Quote's own opening: "When the
         appropriate time comes, please sign the proposal and submit payment by clicking here. ..." It replaced "The
         ordering instructions are written on the second page of the quote. ... the quickest payment method is to use a
         card by clicking here." The link is the sign page, so the old separate "review and sign it online" line went too.
         ⛔ NO PAYMENT SENTENCE WHILE A COST IS STILL PENDING (Ryan 2026-09-13, freight OR installation OR both): it is an
         invitation to PAY a total that is not final yet. The quote still goes; the email simply does not push them toward
         paying it. `value`, `free` and `none` all keep it; only `pending` means "a figure is coming". */
      var _costPending = ship.status === 'pending' || inst.status === 'pending';
      var _ps = pay.p ? B.paymentSentence(pay.p, { lead: 'appropriate' }) : null;
      /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — THE WEIGHTS THEY DESIGNED, on a quote
         that started in the Weight Designer. Built before the paragraph is pushed because where it
         goes depends on what it says: with nothing of ours yet it rides on the END of the ordering
         line (same thought, one sentence later), and once we have made a version it becomes its own
         paragraph. Ryan wrote the line break in himself.
         ⛔ It returns null when the deal has no design, or has one with no link yet, and the email
         then reads exactly as it always has. A template must not refuse to send over this. */
      /* CUSTOM_WDAUTOQUOTE_BY_CLAUDE (Ryan 2026-11-02) - their design and OUR newest version, each by its own
         link, from the same lookup the Inquiry follow-up uses (email-tpl-snippets.js `T.wdDesigns` / `T.wdLink`), so
         the two emails can never point at different designs. The lead's own "Design link" is not on a quote's row;
         the deal's linked files carry the customer's share (written by /wd-quote when the lead was filed). */
      var _wdd = (typeof T.wdDesigns === 'function') ? T.wdDesigns({ ref: ref }) : null;
      var _wdLink = (typeof T.wdLink === 'function') ? T.wdLink : function () { return ''; };
      var _wd = B.weightDesignLine({
        theirsUrl: (_wdd && _wdd.theirs && _wdd.theirs[0]) ? _wdLink(_wdd.theirs[0].designId) : '',
        oursUrl: (_wdd && _wdd.ourShareId) ? _wdLink(_wdd.ourShareId) : '',
      });

      if (_costPending) {
        /* ⛔ SUPPRESSED, WHICH IS A THIRD STATE AND NOT THE FALLBACK. The `else` below is for
           "no quote attached yet" and prints a DIFFERENT ordering sentence plus a warning telling
           the rep to attach the quote — both untrue here, where the quote is attached and the
           point is that we are not asking for payment until the figure is final.
           ⚠️ The weight-design line normally rides on the END of the ordering paragraph, so with
           that paragraph gone it has to become its own or it disappears with it. */
        if (_wd && !_wd.standalone) paras.push(_wd.runs);
        warnings.push((ship.status === 'pending' && inst.status === 'pending')
          ? 'Freight and installation are both still pending, so the payment sentence was left out.'
          : ((ship.status === 'pending' ? 'Freight is' : 'Installation is')
            + ' still pending, so the payment sentence was left out.'));
      } else if (_ps) {
        /* Appended to the SAME run list, so it lands in the same paragraph. */
        paras.push(_wd && !_wd.standalone ? _ps.runs.concat([{ t: ' ' }], _wd.runs) : _ps.runs);
        _ps.warnings.forEach(function (w) { warnings.push(w); });
      } else {
        if (_wd && !_wd.standalone) paras.push(_wd.runs);
        warnings.push('The payment sentence was left out: ' + pay.error);
      }
      if (_wd && _wd.standalone) paras.push(_wd.runs);
      if (_wd) _wd.warnings.forEach(function (w) { warnings.push(w); });

      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-09-01) - the gym layout, when the QW's
         "Layout" box put one on the end of the proposal.

         Ryan: *"On quotes where that is included, after the line of 'When the appropriate time
         comes...', do a line break then add a line which says 'I've also included a room layout
         in the attachment, which you can see on the last page.'"*

         ⛔ READ OFF THE ATTACHED BYTES, NOT OFF THE QUOTE'S SETTINGS. `includeLayoutInPdf` on
         workstation_meta says what the NEXT render will do; this sentence is a claim about the
         document already on the email. They come apart exactly where it matters: a quote
         re-attached from a previous send (the case the ordering block already warns about) can
         carry the flag and no layout pages. So the source is X-BSF-Layout-Pages, the same
         render-stamped channel the page number and the card link come from, and a PDF nobody
         re-rendered simply says nothing.

         ⚠️ ONE PAGE PER SPACE, so a three-space design appends three. "The last page" would then
         name only the last of them, and the plural is the smallest honest fix.

         ALL THE ATTACHED QUOTES OR NONE. "the attachment" is singular and the sentence points at
         one ending; with two quotes attached and a layout on only one it would be false of the
         other. Dropped rather than guessed, and the rep is told why - they can still write it. */
      var _layoutMetas = T.attachedQuoteMeta((ctx && ctx.attachments) || []);
      var _withLayout = _layoutMetas.filter(function (m) { return (m.layoutPages || 0) > 0; });
      if (_withLayout.length && _withLayout.length === _layoutMetas.length) {
        var _layoutPages = _withLayout.reduce(function (n, m) { return Math.max(n, m.layoutPages || 0); }, 0);
        /* CUSTOM_LAYOUTOVERAGE_BY_CLAUDE (Ryan 2026-09-20) — when the drawing SHOWS the overage,
           say what the red means. Ryan's wording, appended to the same paragraph:
           *"The layout takes into consideration the minimum required roll lengths from the
           factory. All material overage is highlighted in red."*
           (Reworded by Ryan 2026-09-20, hours after the first version: "minimum roll cut lengths"
           -> "minimum required roll lengths", and the closing clause dropped "in the layout",
           which the sentence before it had already said.)

           ⛔ `layoutOverage` IS A PROPERTY OF THE ATTACHED BYTES, not of the QW's Overage box —
           the same rule the paragraph above is built on, and here it is not a nicety. Variant
           renders are generated asynchronously on design save, so a quote with the box ticked
           whose `o1_*` variant has not landed yet appends the DEFAULT layout, which has no
           overage on it. Keyed off the checkbox, this would tell a customer to look for red that
           is not in the document. The worker only stamps X-BSF-Layout-Overage: 1 when the
           overage variant is the one it really appended.

           ⚠️ ALL THE ATTACHED QUOTES OR NONE, exactly like the sentence it extends: two quotes
           where only one drawing carries overage would make "the layout" false of the other.
           The base sentence still goes out; only the two extra ones are dropped, and the rep is
           told so they can add them by hand if they know better. */
        var _withOverage = _withLayout.filter(function (m) { return m.layoutOverage === true; });
        var _layoutText = _layoutLead(_withLayout, _layoutPages, false);   // CUSTOM_LAYOUTPLURAL_BY_CLAUDE
        if (_withOverage.length && _withOverage.length === _withLayout.length) {
          /* CUSTOM_ROLLSENTENCE_BY_CLAUDE (Ryan 2026-09-24) — the roll-lengths sentence only on a
             quote that carries a roll; see _overageSentences. */
          _layoutText += _overageSentences(perQuote, index);
        } else if (_withOverage.length) {
          warnings.push('Only ' + _withOverage.length + ' of the ' + _withLayout.length
            + ' attached layouts shows the overage, so the lines about roll lengths and the red'
            + ' highlighting were left out.');
        }
        paras.push([{ t: _layoutText }]);
      } else if (_withLayout.length) {
        warnings.push('Only ' + _withLayout.length + ' of the ' + _layoutMetas.length
          + ' attached quotes carries the gym layout, so the line about the room layout was left out.');
      }
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — the open-door line, moved BELOW the
         ordering instructions and reworded.

         It used to sit above them and read "Please confirm receipt, and let me know if you have
         any questions once you review the quote." Two problems: it asked for a task (confirm
         receipt) in the middle of an email whose last word is a payment link, and it interrupted
         the flow from what the quote covers straight into where the instructions are. Now the
         email states the freight, points at the ordering page, and only then opens the door.

         No longer varies with the attachment count, so it dropped `quoteWord`.

         CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-09-01) — reworded again, from "Lastly, if you
         have any questions, please don't hesitate to reach out." It ends the email expecting a
         reply rather than merely permitting one, which is the same reason every rung of the
         follow-up ladder ends on a question. */
      /* ============================================
         CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-13) — WHERE THE INSTALL INSTRUCTIONS LIVE
         ============================================
         When the quote carries flooring and NO installation, the customer is fitting it
         themselves, so the email says where the manual is before it asks for their thoughts.

         ⛔ "NO INSTALLATION EITHER PENDING OR ADDED" IS `inst.status === 'none'` AND NOTHING
         ELSE. The fee has four states and three of them mean installation is part of the
         conversation: `value` (a price is on the quote), `free` ("included at no additional
         charge"), and `pending` (being quoted — the email has already promised to send it over).
         Only `none` means no installation line exists at all. This was measured against
         `_feeState`, not guessed.

         ⛔ "FLOORING" IS OPERATIONALLY "A QUOTED PRODUCT THAT HAS A TECH MANUAL", which is the
         only definition that can be acted on: a flooring item with no manual has nothing to link
         to, and the sentence would be an offer we cannot keep.

         ⚠️ ONE ENTRY PER MANUAL, named by the biggest line that matched it. Ryan's wording is
         "multiple products each with their own tech manual"; two products under one manual would
         put the same href behind "here" twice in one sentence.

         ⚠️ IT DOES NOT REPLACE THE CLOSING LINE, it goes above it — Ryan: the existing sentence is
         "added as a new line below it". */
      /* ⛔ THIS EMAIL IS RESERVED FOR ROLLS AND TILES (Ryan 2026-09-13, after a live test). A quote
         carrying RageTurf dBTile, Quad Blok connectors and a pail of E-Grip III produced "…here for
         the RageTurf dBTile, here for the Quad Blok, and here for the E-Grip III" — the accessories
         have manuals, and a customer is not reading an adhesive data sheet to lay a floor.

         ⚠️ THE SWITCH IS PER MANUAL, ON THE PAGE (`first_quote`), not a list of names in here — an
         accessory list baked into this file could not be changed without a deploy, and the whole
         index is maintained in the theme editor by design.
         ⚠️ MISSING READS AS TICKED, so every block saved before the field existed keeps working;
         only an explicit `false` excludes.
         ⛔ AND IT DOES NOT TOUCH THE SNIPPET. A rep may still insert any manual by hand, adhesive
         included — this governs the sentence the email writes by itself, which is the one nobody
         chose. */
      var _instLinks = [];
      if (inst.status === 'none' && ecoreIdx) {
        ecoreIdx = { manuals: (ecoreIdx.manuals || []).filter(function (d) { return d && d.fq !== false; }) };
        var _lines = [];
        perQuote.forEach(function (d) {
          var li = (d && d.quote && d.quote.lineItems) || [];
          _lines = _lines.concat(li);
        });
        var _seenDoc = {};
        /* ⛔ `floorOnly` — rolls, tiles, pavers and planks, nothing else. See the long note over
           `_ecoreManualsForQuote`: the test is the `unit:` tag that merchandising already sets,
           which the mention index carries as `uom`, so no accessory list and no checkbox has to be
           kept up to date by hand. The SNIPPET deliberately does not pass it: a rep choosing a
           manual on purpose may choose the adhesive data sheet. */
        T.ecoreManualsForQuote(_lines, ecoreIdx.manuals || [], { floorOnly: true, index: index }).forEach(function (m) {
          if (_seenDoc[m.doc.id]) return;
          _seenDoc[m.doc.id] = 1;
          _instLinks.push({ name: m.name, url: T.ecoreManualUrl(m.doc.title) });
        });
      }
      /* ⛔ "FINALLY," ONLY EARNS ITS PLACE AS THE LAST ITEM IN A SEQUENCE (Ryan 2026-09-13). When
         a pending cost withholds the ordering paragraph above, this becomes the FIRST of the
         closing lines rather than the last, and "Finally," opening a sequence of one reads as if
         a paragraph went missing — which, from the customer's side, it did. */
      var _lead = _costPending ? 'To learn' : 'Finally, to learn';
      if (_instLinks.length === 1) {
        paras.push([
          { t: _lead + ' more about how this product is installed, you can see the installation manual by ' },
          { t: 'clicking here', link: _instLinks[0].url },
          { t: '.' },
        ]);
      } else if (_instLinks.length > 1) {
        /* "here for the X and here for the Y" at two; a serial comma and "and" from three up.
           Built as runs because every "here" is its own hyperlink. */
        var _runs = [{ t: _lead + ' more about how these products are installed, you can view their installation manuals ' }];
        _instLinks.forEach(function (l, i) {
          var last = i === _instLinks.length - 1;
          if (i > 0) {
            if (_instLinks.length === 2) _runs.push({ t: ' and ' });
            else _runs.push({ t: last ? ', and ' : ', ' });
          }
          _runs.push({ t: 'here', link: l.url });
          _runs.push({ t: ' for the ' + l.name });
        });
        _runs.push({ t: '.' });
        paras.push(_runs);
      }

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (AIOPS `e6b615ee9973`, Ryan 2026-09-14) — the closer counts
         too. It was the last hardcoded "the quote" in this template, so a two-quote first send
         attached two PDFs, said "I've attached your quotes" at the top, and then closed by
         asking the customer to review "the quote".
         ⚠️ `quoteWord` is the house source and already drives the opening line: it counts what
         is ACTUALLY ATTACHED (`attachedRefs`), not what the deal holds — see the note above it. */
      paras.push([{ t: 'I look forward to hearing your thoughts and questions once you get a chance to review the ' + quoteWord + '.' }]);
      /* CUSTOM_SIGNOFFAGAIN_BY_CLAUDE (Ryan 2026-09-20) — ⛔ "AGAIN" ONLY WHEN SOMETHING WAS
         ALREADY SAID. Ryan: *"the end of the email signs off with 'Thanks again, [First].' but
         I'm pretty sure a lot of these templates never said 'Thanks' initially to then make
         'Thanks again' make sense — especially when we've never spoken to the person."*

         ⛔ DERIVED FROM THE BRANCH THAT RAN, NOT BY GREPPING THE RENDERED TEXT. Scanning the body
         for the word would count a product called "Thanks" and would break the moment a sentence
         is reworded — and two of these openings have been reworded today alone. Each flag below
         is the same condition that decided the paragraph.

         The four shapes that reach the closer having thanked nobody are: a stale spoken call
         ("As per our last conversation…"), a stale missed call, a stale voicemail, and the case
         where the customer emailed and we had already written back. Those now close "Thanks,
         {First}!" — warm, no claim.

         ⚠️ THE SPOKEN SHAPE COUNTS AS THANKED, and it is the one to look at again if this reads
         oddly: its opener is "Thanks again for your time on the phone", so the email says
         "Thanks again" twice. That is today's behaviour and changing it is a tone call, not a
         correctness one, so it is left as it was. */
      var _thankedAbove = !!(
        coldInquiry                                   // "Thank you for reaching out and inquiring…"
        || (inbound && !repliedToInbound)             // "Thanks very much for your email."
        || missedOpens                                // "Thanks very much for reaching out about…"
        || (call && callWhen && callWhen.recent)      // "Thanks again for your time on the phone…"
      );
      var _closer = _thankedAbove ? 'Thanks again' : 'Thanks';
      paras.push([{ t: first ? _closer + ', ' + first + '!' : _closer + '!' }]);

      if (!details) warnings.push('Could not read ' + (ref || 'the deal') + ', so shipping and item details were left out.');

      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-09-06) — the subject, in the Send Quote shape.
         The Workstation's Send Quote button seeds "Quote for {Name} - Blue Sky Fitness Supply -
         {ref}" before the composer opens; the Deal Dashboard's ✉ pill opens on an empty box, so
         picking this template there left the subject blank. The composer writes whatever a
         template returns here (see _applyTemplate's setSubject) — on the Workstation it rewrites
         the box with the identical string, and on a REPLY the composer refuses the write so the
         thread keeps its name. Same full-name source the inquiry template's subject uses. */
      var _subjectName = B.customerFullName(details, dq, ctx);
      if (!_subjectName) warnings.push('No customer name on this deal, so the subject line reads "Quote - Blue Sky Fitness Supply".');
      var subject = B.sendQuoteSubject(_subjectName, ref);

      return { paragraphs: paras, warnings: warnings, subject: subject };
    });
  }

  T.register({
    id: 'first-quote',
    icon: '\ud83d\udcc4',
    /* CUSTOM_UPDATEDQUOTE_BY_CLAUDE (Ryan 2026-09-24) — renamed from "First quote to a customer"
       when "2nd+ Quote to Customer" arrived. ⛔ THE ID DID NOT MOVE: `first-quote` is what the
       send ledger and the once-only gate are keyed on. */
    label: '1st Quote to Customer',
    hint: 'Thanks for the call, the attached quote, freight, the payment link',
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-29) — the composer fetches and attaches the
       deal's quote PDF before this builds, if it is not already on the email. It has to: the
       layout and install facts are read off that PDF's own X-BSF-* response headers (the payment
       link no longer is: since 2026-10-27 it comes from GET /deal-pay-link). The QW's
       Send Quote has always pre-attached; the DD's ✉ pill did not, and picking the template there
       produced an email with no quote and no link. See _ensureQuotePdfAttached. */
    needsQuotePdf: true,
    build: _buildFirstQuote,
    gate: function (st) {
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — a quote ATTACHED to this very email
         satisfies this as surely as a flipped status does, and it is the only thing that can
         be true on the Send-Quote screen: the status flips BECAUSE of the send being composed.
         See the note on `quoteAttached` in email-tpl-core.js. */
      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-30) — ⛔ AND A QUOTE THAT MERELY EXISTS
         OPENS IT TOO, WHICH IS THE WHOLE POINT OF THE TEMPLATE. Ryan: *"I want to be able to
         download the quote essentially for the first time from the email pop-up sort of thing --
         so that I don't need to download then email, I can email first."*

         `needsQuotePdf` already makes picking this template fetch `/pdf` and attach the result,
         and `_ensureQuotePdfAttached`'s own note says *"asking for the quote template IS the
         signal that the rep wants the quote"* — that render is what stamps `pdfDownloaded` and
         `quotedAt`. So on the Deal Dashboard the old pair asked for the very state that picking
         this template CREATES, and a `disabled` row cannot be picked: RW-1333-A had a saved,
         priced quote and no way to reach it from the ✉ pill at all.

         ⚠️ Ordered last of the three deliberately — `quoted` and `quoteAttached` are cheap field
         reads and this walks the chain. ⛔ And it is NOT a relaxation of the once-only rule: the
         two checks below are the guards against sending a first quote twice, they are separate,
         and they still run. See `_chainQuotable` in email-tpl-core.js. */
      var _noQuote = _noQuoteToSend(st);
      if (_noQuote) return _noQuote;

      /* CUSTOM_UPDATEDQUOTE_BY_CLAUDE (Ryan 2026-09-24) — ⛔ STRICTLY ONCE PER CUSTOMER AGAIN.
         From 2026-08-29 to today this gate REOPENED when freight or install had been applied
         since the last PDF went out (`freightStatus === 'quoted' && freightSynced === false`, and
         the install pair), because re-sending the updated total was a normal Deal Dashboard step
         and this was the only quote email there was. It is not any more: that case is exactly
         what "2nd+ Quote to Customer" (`updated-quote`, below) is for, and Ryan's call was that
         the first-quote wording — "Thank you for reaching out and inquiring…" — must not go to a
         customer a second time. So the two once-only checks are unconditional. */
      if (st.wasSent('first-quote')) return 'Already sent to this customer — use "2nd+ Quote to Customer".';
      /* The pre-ledger case: a quote PDF has demonstrably been emailed on this chain. */
      if (T.quotePdfAlreadyEmailed(st.ref)) return 'A quote has already been emailed on this deal — use "2nd+ Quote to Customer".';
      return null;
    },
  });

  /* ============================================
     CUSTOM_UPDATEDQUOTE_BY_CLAUDE (Ryan 2026-09-24) — "2nd+ Quote to Customer"
     ============================================
     Ryan: *"a shorter sweeter version of first quote to customer… Doesn't need to mention that
     freight is included to their state, but should include the floor plan on the last page if
     we're including a floor plan. Needs to be worded in a way that sounds like a follow-up quote
     based on an email they sent or a phone call we had."*

     THE SHAPE (his sketch, kept): Hi {First} · [Thanks for your email. | Thanks again for your
     time on the phone {when}.] I've attached an updated quote for the {product}{ in the X color}.
     · To finalize your order, please sign the proposal and submit payment by clicking here. We have
     a few different payment methods ... (B.paymentSentence, since 2026-10-27) · [floor-plan line] · Please let me know if you have
     any questions. · Thanks{ again}, {First}!

     ⛔ WHAT IT DOES NOT SAY, ON PURPOSE: the freight-to-your-state sentence, the ECORE install
     manual links and the Weight Designer line all belong to the first quote, which this customer
     has already had. The opening keeps only the two acknowledgements that are about a NEW thing
     (an unanswered email of theirs, a recent call); the cold-inquiry and missed-call openings are
     first-contact sentences and would be false here.

     THE FLOOR PLAN, in Ryan's three cases: *"If the same floor plan existed on the previous quote,
     no need to include it. If the floor plan on quote 2 is different, make note of attaching an
     updated layout. If there was no layout on quote 1 but there is one on this quote, then you
     can use the line you used."* "What the customer already holds" is `_layout` on the send
     ledger (the worker's recordQuoteLayoutSent), written by every send that carried a stamped
     quote PDF; "what this email carries" is the attached render's X-BSF-Layout-* headers. The two
     are compared by the drawing's byte digest, so a re-save with no visual change is "the same".
     ⚠️ When the history cannot be read — a quote went out before the ledger learned about
     layouts, or through Outlook by hand — the email uses the first-time sentence and tells the
     rep why. Redundant beats a false "same", and silent is not an option for a claim about a
     document. */
  function _buildUpdatedQuote(ctx) {
    var ref = String((ctx && ctx.ref) || '').trim();
    var atts = (ctx && ctx.attachments) || [];
    var attachedRefs = _attachedQuoteRefs(atts, ref);
    var readRefs = attachedRefs.length ? attachedRefs : (ref ? [ref] : []);
    return Promise.all([
      Promise.all(readRefs.map(_loadDetails)),
      _loadDetails(ref),
      _loadMentionIndex(),
      _ensureChain(ref),
      T.loadTemplatesSent(ref),     // the ledger: what floor plan the customer already holds
      /* 💳 the payment sentence's link and terms (Ryan 2026-10-27); a refusal (fees pending, closed) resolves to its
         reason so the email still builds without the sentence. */
      B.payLinkFor(ctx).then(function (p) { return { p: p }; }, function (e) { return { error: (e && e.message) || 'the payment link could not be read.' }; }),
    ]).then(function (res) {
      var perQuote = (res[0] || []).filter(Boolean);
      var details = res[1] || perQuote[0] || null;
      var index = res[2] || [];
      var sent = res[4] || {};
      var pay = res[5] || {};
      var quote = (details && details.quote) || null;
      var wm = (details && details.workstationMeta) || null;
      var dq = _dealByRef(ref);
      var warnings = [];

      var fullName = (quote && quote.customer && quote.customer.name) || (dq && dq.customer) || '';
      var first = _firstName(fullName);
      if (!first) {
        var to = (Array.isArray(ctx && ctx.toEmails) ? ctx.toEmails : [])[0] || '';
        first = _firstName(String(to).split('@')[0].replace(/[._-]+/g, ' '));
      }

      var plural = attachedRefs.length > 1;
      var quoteWord = plural ? 'quotes' : 'quote';

      var _items = _headlineItems(perQuote, index, warnings);
      var tops = _items.tops, colorPhrase = _items.colorPhrase;

      /* The opening — the first-quote email's two "something new happened" rules, unchanged:
         an inbound email of theirs still waiting on us is thanked (no date — the thanks is for
         the message); otherwise a RECENT spoken call is; a call they have since written about,
         or one older than the house ladder can name, opens nothing. Same reads, same order. */
      var chain = _chainDeals(ref).concat(dq ? [dq] : []);
      var custAddrs = T.customerAddrs(quote && quote.customer, dq, ctx && ctx.toEmails);
      var call = _lastSpokenCall(chain);
      var callWhen = call ? _whenPhrase(call.ts, T.now()) : null;
      var inbound = _lastCustomerInboundAfter(chain, call ? call.ts : 0, custAddrs);
      var repliedToInbound = inbound ? !!_lastCustomerOutboundAfter(chain, inbound.ts, custAddrs) : false;

      var paras = [];
      paras.push([{ t: first ? 'Hi ' + first + ',' : 'Hi there,' }]);

      /* "I've attached an updated quote for the X in the Y color." — the product and colour
         clause are the first-quote email's own (`_headlineItems`), so the two emails name the
         same item the same way. Two attached revisions read "your updated quotes for the A and B". */
      var attachRuns = tops.length
        ? [{ t: "I've attached " + (plural ? 'your updated quotes' : 'an updated quote') + ' for the ' }]
            .concat(_joinRuns(tops), [{ t: colorPhrase + '.' }])
        : [{ t: "I've attached " + (plural ? 'your updated quotes.' : 'an updated quote.') }];
      var thanked = false;
      if (inbound) {
        if (!repliedToInbound) {
          paras.push([{ t: 'Thanks very much for your email. ' }].concat(attachRuns));
          thanked = true;
        } else {
          paras.push(attachRuns);
        }
      } else if (call && callWhen && callWhen.recent) {
        paras.push([{ t: 'Thanks again for your time on the phone ' + callWhen.phrase + '. ' }].concat(attachRuns));
        thanked = true;
      } else {
        paras.push(attachRuns);
      }

      /* 💳 The payment sentence (Ryan 2026-10-27), the snippet's plain "To finalize your order" opening. Same rule as the
         first quote while a cost is still pending: the quote goes, the email does not push them toward paying it. */
      var liveFees = _liveFees(ref);
      var feeSource = liveFees ? { rawFees: liveFees } : wm;
      var ship = _feeState(feeSource, 'Shipping & Freight', quote ? quote.shippingAmount : null);
      var inst = _feeState(feeSource, 'Installation', quote ? quote.installationAmount : null);
      var _costPending = ship.status === 'pending' || inst.status === 'pending';
      var _ps = pay.p ? B.paymentSentence(pay.p) : null;
      if (_costPending) {
        warnings.push((ship.status === 'pending' && inst.status === 'pending')
          ? 'Freight and installation are both still pending, so the payment sentence was left out.'
          : ((ship.status === 'pending' ? 'Freight is' : 'Installation is')
            + ' still pending, so the payment sentence was left out.'));
      } else if (_ps) {
        paras.push(_ps.runs);
        _ps.warnings.forEach(function (w) { warnings.push(w); });
      } else {
        warnings.push('The payment sentence was left out: ' + pay.error);
      }

      /* ── the floor plan, in Ryan's three cases ─────────────────────────────
         Read off the attached BYTES (X-BSF-Layout-*), never off the quote's own toggle — the same
         rule the first-quote email is built on, and the same "all the attached quotes or none"
         guard, because "the attachment" is singular. */
      var _layoutMetas = T.attachedQuoteMeta(atts);
      var _withLayout = _layoutMetas.filter(function (m) { return (m.layoutPages || 0) > 0; });
      if (_withLayout.length && _withLayout.length === _layoutMetas.length) {
        var _pages = _withLayout.reduce(function (n, m) { return Math.max(n, m.layoutPages || 0); }, 0);
        var _withOverage = _withLayout.filter(function (m) { return m.layoutOverage === true; });
        var _overageAll = _withOverage.length === _withLayout.length;
        var _keys = [];
        _withLayout.forEach(function (m) {
          var k = String(m.layoutKey || '');
          if (_keys.indexOf(k) === -1) _keys.push(k);
        });
        var _thisKey = (_keys.length === 1) ? _keys[0] : '';
        var last = (sent && sent._layout && typeof sent._layout === 'object') ? sent._layout : null;

        /* Was there a quote before this one at all? With nothing in the ledger and no quote
           PDF on the thread, "no layout on quote 1" is literally true — there was no quote 1
           through this composer — and the first-time sentence needs no caveat. */
        var _anyPrior = !!(last || (sent && sent['first-quote']) || T.quotePdfAlreadyEmailed(ref));

        var _overageTail = _overageAll ? _overageSentences(perQuote, index) : '';
        var _firstTime = _layoutLead(_withLayout, _pages, false) + _overageTail;   // CUSTOM_LAYOUTPLURAL_BY_CLAUDE
        var _updated = _layoutLead(_withLayout, _pages, true) + _overageTail;

        if (last && last.pages > 0 && last.key && _thisKey && last.key === _thisKey) {
          /* Same drawing, already in their hands: nothing to say. Ryan's first case. */
        } else if (last && last.pages > 0 && last.key && _thisKey) {
          paras.push([{ t: _updated }]);
        } else if (last && last.pages === 0) {
          paras.push([{ t: _firstTime }]);
        } else if (!_anyPrior) {
          paras.push([{ t: _firstTime }]);
        } else {
          /* A quote went out before, and either the ledger never saw its layout (sent before
             2026-09-24, or by hand through Outlook) or this render carries no fingerprint (an
             older worker). The honest fallback is the first-time sentence — redundant at worst —
             and the rep is told so they can trim it or change it to "updated". */
          paras.push([{ t: _firstTime }]);
          warnings.push('Could not tell whether the customer already has this floor plan (no layout on record for the earlier quote), so the line reads as a first-time layout. Change it to "an updated room layout" or delete it if they have it already.');
        }
        if (_withOverage.length && !_overageAll) {
          warnings.push('Only ' + _withOverage.length + ' of the ' + _withLayout.length
            + ' attached layouts shows the overage, so the lines about roll lengths and the red'
            + ' highlighting were left out.');
        }
      } else if (_withLayout.length) {
        warnings.push('Only ' + _withLayout.length + ' of the ' + _layoutMetas.length
          + ' attached quotes carries the gym layout, so the line about the room layout was left out.');
      }

      /* Ryan's closer, verbatim. "Thanks again" only when something above thanked them —
         derived from the branch that ran, the same way the first-quote email does it. */
      paras.push([{ t: 'Please let me know if you have any questions.' }]);
      var _closer = thanked ? 'Thanks again' : 'Thanks';
      paras.push([{ t: first ? _closer + ', ' + first + '!' : _closer + '!' }]);

      if (!details) warnings.push('Could not read ' + (ref || 'the deal') + ', so item details were left out.');

      var _subjectName = B.customerFullName(details, dq, ctx);
      if (!_subjectName) warnings.push('No customer name on this deal, so the subject line reads "Updated quote - Blue Sky Fitness Supply".');
      var subject = B.sendQuoteSubject(_subjectName, ref, { updated: true });

      return { paragraphs: paras, warnings: warnings, subject: subject };
    });
  }

  T.register({
    id: 'updated-quote',
    icon: '🔁',
    label: '2nd+ Quote to Customer',
    hint: 'The updated quote, the payment link, the floor plan only if it changed',
    /* Same reason as the first quote: the floor-plan facts are read off the attached render's
       own headers, so the composer fetches and attaches
       the deal's PDF before this builds when nothing is attached yet. */
    needsQuotePdf: true,
    build: _buildUpdatedQuote,
    gate: function (st) {
      /* Open whenever there is a quote to send — built, attached, or buildable — and NEVER
         once-only: a second, third and fourth quote are exactly what it is for. It is not shut
         until the first quote has gone out either (Ryan 2026-09-24): a first quote sent by hand
         from Outlook leaves no ledger line, and a rep who knows this is the second one should not
         be told otherwise by a record that was never written. */
      return _noQuoteToSend(st);
    },
  });
})();
